import React, { useState, useEffect, useRef, useCallback } from 'react';
import Editor from '@monaco-editor/react';
import { initDuckDB } from '../sql/editor/SQLEditor';
import { useTheme } from '../context/ThemeContext';
import {
    Upload, Database, Table2, Trash2, Play, ChevronDown, ChevronRight,
    X, FileText, AlertCircle, CheckCircle2, Loader2, Plus, PenLine
} from 'lucide-react';

// ── IndexedDB helpers ────────────────────────────────────────────────────────
const IDB_NAME = 'sandbox_store';
const IDB_VERSION = 1;
const IDB_STORE = 'csv_files';

function openSandboxDB() {
    return new Promise((resolve, reject) => {
        const req = indexedDB.open(IDB_NAME, IDB_VERSION);
        req.onerror = () => reject(req.error);
        req.onsuccess = () => resolve(req.result);
        req.onupgradeneeded = (e) => {
            const db = e.target.result;
            if (!db.objectStoreNames.contains(IDB_STORE)) {
                db.createObjectStore(IDB_STORE, { keyPath: 'name' });
            }
        };
    });
}

async function idbGetAll() {
    const db = await openSandboxDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(IDB_STORE, 'readonly');
        const req = tx.objectStore(IDB_STORE).getAll();
        req.onerror = () => reject(req.error);
        req.onsuccess = () => resolve(req.result || []);
    });
}

async function idbPut(record) {
    const db = await openSandboxDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(IDB_STORE, 'readwrite');
        const req = tx.objectStore(IDB_STORE).put(record);
        req.onerror = () => reject(req.error);
        req.onsuccess = () => resolve();
    });
}

async function idbDelete(name) {
    const db = await openSandboxDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(IDB_STORE, 'readwrite');
        const req = tx.objectStore(IDB_STORE).delete(name);
        req.onerror = () => reject(req.error);
        req.onsuccess = () => resolve();
    });
}

// ── Format cell values (detect Unix timestamps) ──────────────────────────────
function formatDisplayValue(val) {
    if (val === null || val === undefined) return null;
    let num = typeof val === 'bigint' ? Number(val) : val;
    if (typeof num === 'string' && !isNaN(num)) num = Number(num);
    // Unix timestamp in seconds or milliseconds
    if (typeof num === 'number' && num > 1_000_000_000 && num < 10_000_000_000_000) {
        const ms = num > 10_000_000_000 ? num : num * 1000;
        const d = new Date(ms);
        // If UTC time is exactly midnight it is a DATE value - show date only
        if (d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && d.getUTCSeconds() === 0 && d.getUTCMilliseconds() === 0) {
            return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });
        }
        return d.toLocaleString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric',
            hour: '2-digit', minute: '2-digit', second: '2-digit',
        });
    }
    return String(val);
}

// ── CSV filename -> DuckDB table name ─────────────────────────────────────────
function toTableName(filename) {
    return filename.replace(/\.csv$/i, '').replace(/[^a-zA-Z0-9_]/g, '_');
}

// ── Results table component ──────────────────────────────────────────────────
const ResultsTable = React.memo(({ results, error, loading, executionTime }) => {
    const [displayLimit, setDisplayLimit] = useState(100);

    useEffect(() => setDisplayLimit(100), [results]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-40 text-gray-500 dark:text-gray-400 gap-2">
                <Loader2 size={18} className="animate-spin" /> Running query...
            </div>
        );
    }
    if (error) {
        return (
            <div className="p-4">
                <div className="flex items-start gap-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800/40 rounded-lg p-4">
                    <AlertCircle size={18} className="text-red-500 dark:text-red-400 mt-0.5 shrink-0" />
                    <div>
                        <p className="text-red-600 dark:text-red-300 font-semibold text-sm mb-1">Query Error</p>
                        <pre className="text-red-500 dark:text-red-400 text-xs font-mono whitespace-pre-wrap break-words leading-relaxed">{error}</pre>
                    </div>
                </div>
            </div>
        );
    }
    if (!results) {
        return (
            <div className="flex flex-col items-center justify-center h-40 text-gray-400 dark:text-gray-500 gap-2">
                <Play size={24} className="opacity-30" />
                <p className="text-sm">Run a query to see results</p>
            </div>
        );
    }
    if (results.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center h-40 text-gray-400 dark:text-gray-500 gap-2">
                <CheckCircle2 size={24} className="opacity-30" />
                <p className="text-sm">Query returned 0 rows</p>
            </div>
        );
    }

    const cols = Object.keys(results[0]);
    const displayed = results.slice(0, displayLimit);
    const hasMore = results.length > displayLimit;

    return (
        <div className="flex flex-col h-full overflow-hidden">
            <div className="flex-1 overflow-auto">
                <table className="w-full min-w-max text-sm">
                    <thead className="sticky top-0 bg-gray-100 dark:bg-[#0f172a] border-b border-gray-200 dark:border-gray-700 z-10">
                        <tr>
                            <th className="px-3 py-2 text-left text-xs text-gray-500 font-medium w-10 border-r border-gray-200 dark:border-gray-700">#</th>
                            {cols.map(col => (
                                <th key={col} className="px-3 py-2 text-left text-xs text-gray-500 dark:text-gray-400 font-semibold uppercase tracking-wide border-r border-gray-200 dark:border-gray-700 last:border-r-0 whitespace-nowrap">
                                    {col}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                        {displayed.map((row, i) => (
                            <tr key={i} className="hover:bg-gray-50 dark:hover:bg-white/5 transition-colors">
                                <td className="px-3 py-2 text-xs text-gray-400 dark:text-gray-600 border-r border-gray-100 dark:border-gray-800">{i + 1}</td>
                                {cols.map(col => {
                                    const v = row[col];
                                    const display = v === null || v === undefined
                                        ? <span className="text-gray-400 dark:text-gray-600 italic">null</span>
                                        : formatDisplayValue(v);
                                    return (
                                        <td key={col} className="px-3 py-2 text-gray-700 dark:text-gray-300 font-mono whitespace-nowrap border-r border-gray-100 dark:border-gray-800 last:border-r-0 max-w-[300px] overflow-hidden text-ellipsis">
                                            {display}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center text-xs text-gray-500 shrink-0">
                <span className="font-mono">
                    {results.length} {results.length === 1 ? 'row' : 'rows'}
                    {executionTime && <span className="ml-3 text-gray-600"> {executionTime}s</span>}
                    {hasMore && <span className="ml-2 text-gray-600">(showing {displayLimit})</span>}
                </span>
                {hasMore && (
                    <button
                        onClick={() => setDisplayLimit(p => p + 100)}
                        className="text-secondary hover:text-accent font-medium transition-colors"
                    >
                        Load more
                    </button>
                )}
            </div>
        </div>
    );
});
ResultsTable.displayName = 'ResultsTable';

// ── TableCard component ──────────────────────────────────────────────────────
const TableCard = ({ table, onRemove, onInsert }) => {
    const [expanded, setExpanded] = useState(false);

    return (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            <div
                className="flex items-center gap-2 px-3 py-2.5 bg-blue-50 dark:bg-[#1a2744] cursor-pointer hover:bg-blue-100 dark:hover:bg-[#1e2f50] transition-colors select-none"
                onClick={() => setExpanded(p => !p)}
            >
                {expanded ? <ChevronDown size={14} className="text-gray-500 dark:text-gray-400 shrink-0" /> : <ChevronRight size={14} className="text-gray-500 dark:text-gray-400 shrink-0" />}
                <Table2 size={14} className="text-secondary shrink-0" />
                <span className="text-gray-800 dark:text-gray-200 text-sm font-mono font-medium flex-1 truncate">{table.name}</span>
                <span className="text-gray-500 text-xs shrink-0">{table.rowCount?.toLocaleString() ?? '?'} rows</span>
                <button
                    onClick={e => { e.stopPropagation(); onInsert(table.name); }}
                    title="Insert SELECT into editor"
                    className="text-gray-500 hover:text-secondary transition-colors ml-1 shrink-0"
                >
                    <Plus size={13} />
                </button>
                <button
                    onClick={e => { e.stopPropagation(); onRemove(table.name); }}
                    title="Remove table"
                    className="text-gray-500 hover:text-red-400 transition-colors shrink-0"
                >
                    <Trash2 size={13} />
                </button>
            </div>
            {expanded && (
                <div className="px-3 py-2 bg-white dark:bg-[#0f172a] border-t border-gray-100 dark:border-gray-700/50 space-y-0.5">
                    {table.columns.map(col => (
                        <div key={col.name} className="flex items-center gap-2 py-0.5">
                            <span className="text-gray-500 dark:text-gray-400 text-xs font-mono flex-1 truncate">{col.name}</span>
                            <span className="text-gray-400 dark:text-gray-600 text-xs shrink-0">{col.type}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// ── Tab helpers ───────────────────────────────────────────────────────────────
let _tabCounter = 1;
function newTab(query = '') {
    const id = `tab_${Date.now()}_${_tabCounter++}`;
    return { id, name: `Query ${_tabCounter - 1}`, query, results: null, queryError: null, executionTime: null };
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function DataSandbox() {
    const { theme } = useTheme();
    const [dbReady, setDbReady] = useState(false);
    const [initError, setInitError] = useState(null);
    const [tables, setTables] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [isDragOver, setIsDragOver] = useState(false);

    // ── Tabs state ────────────────────────────────────────────────────────────
    const DEFAULT_QUERY = '-- Upload a CSV above, then query it here\nSELECT * FROM your_table LIMIT 100;';
    const [tabs, setTabs] = useState(() => [newTab(DEFAULT_QUERY)]);
    const [activeTabId, setActiveTabId] = useState(() => tabs[0].id); // eslint-disable-line react-hooks/exhaustive-deps
    const [runningTabId, setRunningTabId] = useState(null); // only one query at a time
    const [renamingTabId, setRenamingTabId] = useState(null);
    const [renameValue, setRenameValue] = useState('');

    const activeTab = tabs.find(t => t.id === activeTabId) || tabs[0];

    const editorRef = useRef(null);
    const connRef = useRef(null);
    const dbRef = useRef(null);
    const fileInputRef = useRef(null);
    const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.userAgent);

    // ── Init DuckDB + restore cached tables ──────────────────────────────────
    useEffect(() => {
        let cancelled = false;
        async function init() {
            try {
                const { db, conn } = await initDuckDB();
                if (cancelled) return;
                connRef.current = conn;
                dbRef.current = db;
                setDbReady(true);
                const cached = await idbGetAll();
                const loaded = [];
                for (const record of cached) {
                    try {
                        await db.registerFileText(record.name, record.csvText);
                        const tbl = toTableName(record.name);
                        await conn.query(`CREATE OR REPLACE TABLE "${tbl}" AS SELECT * FROM read_csv_auto('${record.name}')`);
                        loaded.push(record.name);
                    } catch (e) {
                        console.warn('Failed to restore cached file:', record.name, e);
                    }
                }
                if (loaded.length > 0 && !cancelled) await refreshTables(conn);
            } catch (e) {
                if (!cancelled) setInitError(e.message);
            }
        }
        init();
        return () => { cancelled = true; };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Refresh table metadata ────────────────────────────────────────────────
    const refreshTables = useCallback(async (conn) => {
        if (!conn) return;
        try {
            // Only show tables owned by the sandbox (tracked in IndexedDB)
            const cached = await idbGetAll();
            const ownedTableNames = new Set(cached.map(r => toTableName(r.name)));

            const tbls = await conn.query(`SHOW TABLES`);
            const tblRows = tbls.toArray().map(r => r.toJSON ? r.toJSON() : { name: String(r) });
            const tableList = [];
            for (const tblRow of tblRows) {
                const tblName = tblRow.name || tblRow.Name || Object.values(tblRow)[0];
                if (!tblName || !ownedTableNames.has(tblName)) continue;
                try {
                    const schema = await conn.query(`DESCRIBE "${tblName}"`);
                    const columns = schema.toArray().map(r => {
                        const j = r.toJSON ? r.toJSON() : {};
                        return { name: j.column_name || j.name || Object.values(j)[0], type: j.column_type || j.type || '' };
                    });
                    const countRes = await conn.query(`SELECT COUNT(*) as n FROM "${tblName}"`);
                    const n = countRes.toArray()[0]?.n ?? 0;
                    tableList.push({ name: tblName, columns, rowCount: Number(n) });
                } catch (_) {
                    tableList.push({ name: tblName, columns: [], rowCount: null });
                }
            }
            setTables(tableList);
        } catch (e) {
            console.error('refreshTables error:', e);
        }
    }, []);

    // ── Process uploaded files ────────────────────────────────────────────────
    const processFiles = useCallback(async (fileList) => {
        if (!dbReady || !connRef.current || !dbRef.current) return;
        setUploading(true);
        setUploadError(null);
        const errors = [];
        let firstTable = null;
        for (const file of fileList) {
            if (!file.name.endsWith('.csv')) { errors.push(`${file.name}: only .csv files are supported`); continue; }
            try {
                const csvText = await file.text();
                await idbPut({ name: file.name, csvText, uploadedAt: Date.now() });
                await dbRef.current.registerFileText(file.name, csvText);
                const tbl = toTableName(file.name);
                await connRef.current.query(`CREATE OR REPLACE TABLE "${tbl}" AS SELECT * FROM read_csv_auto('${file.name}')`);
                if (!firstTable) firstTable = tbl;
            } catch (e) {
                errors.push(`${file.name}: ${e.message}`);
            }
        }
        await refreshTables(connRef.current);
        setUploading(false);
        if (errors.length > 0) setUploadError(errors.join('\n'));
        // Update the active tab's query to a helpful starter
        if (firstTable) {
            const snippet = `SELECT * FROM "${firstTable}" LIMIT 100;`;
            updateTabQuery(activeTabId, snippet);
            if (editorRef.current) editorRef.current.setValue(snippet);
        }
    }, [dbReady, refreshTables, activeTabId]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Remove table ──────────────────────────────────────────────────────────
    const removeTable = useCallback(async (tableName) => {
        if (!connRef.current) return;
        try {
            await connRef.current.query(`DROP TABLE IF EXISTS "${tableName}"`);
            await idbDelete(tableName + '.csv');
        } catch (e) { console.warn('removeTable:', e); }
        await refreshTables(connRef.current);
    }, [refreshTables]);

    // ── Tab management ────────────────────────────────────────────────────────
    const updateTabQuery = (tabId, query) => {
        setTabs(prev => prev.map(t => t.id === tabId ? { ...t, query } : t));
    };

    const addTab = useCallback(() => {
        const t = newTab(DEFAULT_QUERY);
        setTabs(prev => [...prev, t]);
        setActiveTabId(t.id);
        // Editor content will be set by the activeTabId effect below
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const closeTab = useCallback((tabId, e) => {
        e.stopPropagation();
        setTabs(prev => {
            if (prev.length === 1) return prev; // keep at least one tab
            const idx = prev.findIndex(t => t.id === tabId);
            const next = prev.filter(t => t.id !== tabId);
            setActiveTabId(curr => {
                if (curr !== tabId) return curr;
                // activate the tab to the left (or right if first)
                return next[Math.max(0, idx - 1)].id;
            });
            return next;
        });
    }, []);

    const switchTab = useCallback((tabId) => {
        // Save current editor content into the current tab before switching
        if (editorRef.current) {
            const currentContent = editorRef.current.getValue();
            setTabs(prev => prev.map(t => t.id === activeTabId ? { ...t, query: currentContent } : t));
        }
        setActiveTabId(tabId);
    }, [activeTabId]);

    // Update editor content when the active tab changes
    useEffect(() => {
        if (editorRef.current) {
            const newContent = tabs.find(t => t.id === activeTabId)?.query ?? '';
            const current = editorRef.current.getValue();
            if (current !== newContent) editorRef.current.setValue(newContent);
        }
    }, [activeTabId]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Insert SELECT snippet into active tab's editor ────────────────────────
    const insertSelect = useCallback((tableName) => {
        const snippet = `SELECT * FROM "${tableName}" LIMIT 100;`;
        if (editorRef.current) {
            const current = editorRef.current.getValue().trim();
            editorRef.current.setValue(current ? current + '\n\n' + snippet : snippet);
        } else {
            updateTabQuery(activeTabId, snippet);
        }
    }, [activeTabId]);

    // ── Execute query for the active tab ──────────────────────────────────────
    const runQuery = useCallback(async () => {
        if (!dbReady || !connRef.current || runningTabId) return;
        const sql = editorRef.current ? editorRef.current.getValue() : activeTab.query;
        if (!sql.trim()) return;

        const tabId = activeTabId;
        setRunningTabId(tabId);
        setTabs(prev => prev.map(t => t.id === tabId
            ? { ...t, queryError: null, results: null, executionTime: null }
            : t));

        const start = performance.now();
        try {
            const result = await connRef.current.query(sql);
            const colNames = result.schema.fields.map(f => f.name);
            const seen = new Map();
            const uniqueNames = colNames.map(n => {
                const c = (seen.get(n) || 0) + 1;
                seen.set(n, c);
                return c > 1 ? `${n}_${c}` : n;
            });
            const rows = [];
            for (let i = 0; i < result.numRows; i++) {
                const row = {};
                colNames.forEach((col, idx) => {
                    const val = result.getChildAt(idx)?.get(i) ?? null;
                    row[uniqueNames[idx]] = typeof val === 'bigint' ? Number(val) : val;
                });
                rows.push(row);
            }
            const elapsed = ((performance.now() - start) / 1000).toFixed(2);
            setTabs(prev => prev.map(t => t.id === tabId
                ? { ...t, results: rows, executionTime: elapsed, queryError: null }
                : t));
        } catch (e) {
            setTabs(prev => prev.map(t => t.id === tabId
                ? { ...t, queryError: e.message, results: null }
                : t));
        } finally {
            setRunningTabId(null);
        }
    }, [dbReady, runningTabId, activeTabId, activeTab.query]);

    // ── Drag & drop ───────────────────────────────────────────────────────────
    const onDrop = useCallback((e) => {
        e.preventDefault();
        setIsDragOver(false);
        processFiles(Array.from(e.dataTransfer.files));
    }, [processFiles]);
    const onDragOver = (e) => { e.preventDefault(); setIsDragOver(true); };
    const onDragLeave = () => setIsDragOver(false);

    // ── Monaco setup ──────────────────────────────────────────────────────────
    const runQueryRef = useRef(runQuery);
    useEffect(() => { runQueryRef.current = runQuery; }, [runQuery]);

    const handleEditorMount = useCallback((editor, monaco) => {
        editorRef.current = editor;
        editor.addAction({
            id: 'run-query',
            label: 'Run Query',
            keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter],
            run: () => runQueryRef.current(),
        });
        // Load active tab content
        const content = tabs.find(t => t.id === activeTabId)?.query ?? '';
        if (editor.getValue() !== content) editor.setValue(content);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Rename tab ────────────────────────────────────────────────────────────
    const startRename = (tabId, currentName, e) => {
        e.stopPropagation();
        setRenamingTabId(tabId);
        setRenameValue(currentName);
    };
    const commitRename = () => {
        if (renameValue.trim()) {
            setTabs(prev => prev.map(t => t.id === renamingTabId ? { ...t, name: renameValue.trim() } : t));
        }
        setRenamingTabId(null);
    };

    const isRunning = runningTabId === activeTabId;

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gradient-to-br dark:from-[#0a1220] dark:to-[#1e3a5f] flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-white/10 bg-white/80 dark:bg-transparent">
                <div className="max-w-[1600px] mx-auto flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            <Database size={22} className="text-secondary" />
                            Data Sandbox
                        </h1>
                        <p className="text-gray-500 dark:text-gray-400 text-sm mt-0.5">Upload CSV files and query them instantly in your browser.</p>
                    </div>
                    {!dbReady && !initError && (
                        <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 text-sm">
                            <Loader2 size={16} className="animate-spin" /> Initialising...
                        </div>
                    )}
                    {initError && (
                        <div className="flex items-center gap-2 text-red-400 text-sm">
                            <AlertCircle size={16} /> {initError}
                        </div>
                    )}
                </div>
            </div>

            {/* Main layout */}
            <div className="flex-1 max-w-[1600px] w-full mx-auto flex flex-col lg:flex-row overflow-hidden" style={{ minHeight: 0 }}>

                {/* Sidebar */}
                <aside className="w-full lg:w-72 xl:w-80 shrink-0 border-b lg:border-b-0 lg:border-r border-gray-200 dark:border-white/10 bg-white dark:bg-transparent flex flex-col" style={{ minHeight: 0 }}>
                    {/* Upload drop zone */}
                    <div className="p-4 border-b border-gray-200 dark:border-white/10 shrink-0">
                        <div
                            onDrop={onDrop} onDragOver={onDragOver} onDragLeave={onDragLeave}
                            onClick={() => dbReady && fileInputRef.current?.click()}
                            className={`border-2 border-dashed rounded-xl p-5 text-center transition-all cursor-pointer
                                ${isDragOver ? 'border-secondary bg-secondary/10 scale-[1.01]'
                                    : dbReady ? 'border-gray-300 dark:border-gray-600 hover:border-secondary/60 hover:bg-gray-50 dark:hover:bg-white/5'
                                        : 'border-gray-200 dark:border-gray-700 opacity-50 cursor-not-allowed'}`}
                        >
                            {uploading ? (
                                <div className="flex flex-col items-center gap-2 py-1">
                                    <Loader2 size={22} className="text-secondary animate-spin" />
                                    <p className="text-gray-600 dark:text-gray-300 text-sm">Loading files...</p>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center gap-2 py-1">
                                    <Upload size={22} className={isDragOver ? 'text-secondary' : 'text-gray-400'} />
                                    <p className="text-gray-600 dark:text-gray-300 text-sm font-medium">Drop CSV files here</p>
                                    <p className="text-gray-400 dark:text-gray-500 text-xs">or click to browse</p>
                                </div>
                            )}
                            <input ref={fileInputRef} type="file" accept=".csv" multiple className="hidden"
                                onChange={e => processFiles(Array.from(e.target.files))} />
                        </div>
                        {uploadError && (
                            <div className="mt-2 text-red-400 text-xs bg-red-950/30 border border-red-800/40 rounded-lg p-2 whitespace-pre-wrap">
                                {uploadError}
                            </div>
                        )}
                    </div>

                    {/* Table list */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-2">
                        {tables.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-24 text-gray-400 dark:text-gray-600 text-sm gap-2">
                                <FileText size={20} className="opacity-40" />
                                <p>No tables loaded yet</p>
                            </div>
                        ) : (
                            <>
                                <p className="text-gray-400 dark:text-gray-500 text-xs uppercase tracking-wider font-semibold mb-3">
                                    {tables.length} table{tables.length !== 1 ? 's' : ''}
                                </p>
                                {tables.map(t => (
                                    <TableCard key={t.name} table={t} onRemove={removeTable} onInsert={insertSelect} />
                                ))}
                            </>
                        )}
                    </div>

                    {/* Clear all */}
                    {tables.length > 0 && (
                        <div className="p-4 border-t border-gray-200 dark:border-white/10 shrink-0">
                            <button
                                onClick={async () => { for (const t of tables) await removeTable(t.name); }}
                                className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 border border-gray-200 dark:border-gray-700 hover:border-red-300 dark:hover:border-red-700/50 rounded-lg transition-colors"
                            >
                                <X size={14} /> Clear all tables
                            </button>
                        </div>
                    )}
                </aside>

                {/* Right pane: tab bar + editor + results */}
                <div className="flex-1 flex flex-col min-w-0 min-h-0">

                    {/* Tab bar */}
                    <div className="flex items-center bg-gray-100 dark:bg-[#0a1220]/80 border-b border-gray-200 dark:border-white/10 overflow-x-auto shrink-0">
                        {tabs.map(tab => {
                            const active = tab.id === activeTabId;
                            const tabRunning = runningTabId === tab.id;
                            return (
                                <div
                                    key={tab.id}
                                    onClick={() => switchTab(tab.id)}
                                    className={`group flex items-center gap-1.5 px-3 py-2.5 border-r border-gray-200 dark:border-white/10 cursor-pointer select-none shrink-0 max-w-[180px] transition-colors
                                        ${active
                                            ? 'bg-white dark:bg-[#1a2744] border-b-2 border-b-secondary text-gray-900 dark:text-white'
                                            : 'text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-white dark:hover:bg-white/5'}`}
                                >
                                    {tabRunning && <Loader2 size={11} className="animate-spin text-secondary shrink-0" />}
                                    {!tabRunning && active && <span className="w-1.5 h-1.5 rounded-full bg-green-400 shrink-0" />}

                                    {renamingTabId === tab.id ? (
                                        <input
                                            autoFocus
                                            value={renameValue}
                                            onChange={e => setRenameValue(e.target.value)}
                                            onBlur={commitRename}
                                            onKeyDown={e => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') setRenamingTabId(null); }}
                                            onClick={e => e.stopPropagation()}
                                            className="bg-transparent text-gray-900 dark:text-white text-xs outline-none w-24 border-b border-secondary"
                                        />
                                    ) : (
                                        <span className="text-xs truncate">{tab.name}</span>
                                    )}

                                    {/* rename on double-click */}
                                    <button
                                        onClick={e => startRename(tab.id, tab.name, e)}
                                        className="hidden group-hover:inline-flex text-gray-600 hover:text-gray-300 transition-colors shrink-0"
                                        title="Rename tab"
                                    >
                                        <PenLine size={11} />
                                    </button>

                                    {tabs.length > 1 && (
                                        <button
                                            onClick={e => closeTab(tab.id, e)}
                                            className="hidden group-hover:inline-flex text-gray-600 hover:text-red-400 transition-colors shrink-0"
                                            title="Close tab"
                                        >
                                            <X size={11} />
                                        </button>
                                    )}
                                </div>
                            );
                        })}

                        {/* New tab button */}
                        <button
                            onClick={addTab}
                            className="px-3 py-2.5 text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-white dark:hover:bg-white/5 transition-colors shrink-0"
                            title="New tab"
                        >
                            <Plus size={15} />
                        </button>
                    </div>

                    {/* Editor */}
                    <div className="flex flex-col shrink-0" style={{ height: '300px' }}>
                        <div className="flex-1 overflow-hidden">
                            <Editor
                                language="sql"
                                defaultValue={activeTab.query}
                                onMount={handleEditorMount}
                                onChange={v => {
                                    // Keep the active tab's query in sync (debounced via state)
                                    setTabs(prev => prev.map(t => t.id === activeTabId ? { ...t, query: v || '' } : t));
                                }}
                                options={{
                                    minimap: { enabled: false },
                                    fontSize: 13,
                                    lineNumbers: 'on',
                                    scrollBeyondLastLine: false,
                                    wordWrap: 'on',
                                    padding: { top: 10, bottom: 10 },
                                    fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                                    automaticLayout: true,
                                    tabSize: 2,
                                    quickSuggestions: true,
                                }}
                                theme={theme === 'dark' ? 'vs-dark' : 'vs'}
                            />
                        </div>
                    </div>

                    {/* Run bar */}
                    <div className="flex items-center justify-between px-4 py-2 bg-gray-50 dark:bg-[#0a1220]/60 border-y border-gray-200 dark:border-white/10 shrink-0">
                        <span className="text-xs text-gray-400 dark:text-gray-500 font-mono hidden sm:block">{isMac ? '⌘' : 'Ctrl'}+Enter to run</span>
                        <button
                            onClick={runQuery}
                            disabled={!dbReady || !!runningTabId || tables.length === 0}
                            className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-md shadow-lg transition-all duration-200"
                        >
                            {isRunning
                                ? <><Loader2 size={14} className="animate-spin" /> Running...</>
                                : <><Play size={14} /> Run Query</>
                            }
                        </button>
                    </div>

                    {/* Results */}
                    <div className="flex-1 overflow-hidden bg-gray-50 dark:bg-[#0a1220]/40 flex flex-col min-h-0">
                        <div className="px-4 py-2 border-b border-gray-100 dark:border-white/5 text-xs text-gray-500 uppercase tracking-wider font-semibold shrink-0">
                            Results
                        </div>
                        <div className="flex-1 overflow-hidden">
                            <ResultsTable
                                results={activeTab.results}
                                error={activeTab.queryError}
                                loading={isRunning}
                                executionTime={activeTab.executionTime}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}